// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

// The camera path a hole flyover glides along, kept pure — no React, no clock — so the
// choreography ("start on the tee, ease to the green, hold there a beat") is asserted in
// tests rather than eyeballed in a running animation.

import { distanceMeters, type LatLon } from '@/features/hole/holeview/tileMath';
import { bearingDegrees } from '@/features/hole/holeview/wind';

/** One frame of the flyover: everything `computeLayoutAt` needs besides the viewport. */
export type HoleCameraFrame = {
  center: LatLon;
  spanMeters: number;
  bearingDegrees: number;
};

export type HoleCameraPath = {
  /** The glide from tee to green, excluding the hold. */
  durationMs: number;
  /** The pause on the green before a player (or the auto-advance) moves on. */
  holdMs: number;
  /** durationMs + holdMs — what a playback loop counts up to. */
  totalMs: number;
  /** The hole length the path was built from, for the caption readout. */
  holeLengthMeters: number;
  cameraAt: (tMs: number) => HoleCameraFrame;
};

/**
 * Ground covered by the viewport's width, as a share of the hole's length. Narrow enough
 * that the camera visibly travels, wide enough that the fairway corridor and its trouble
 * stay in frame either side of the play line.
 */
const SPAN_SHARE_OF_LENGTH = 0.55;
/** A short par 3 still needs room for tee, green and the ground between. */
const MIN_SPAN_METERS = 140;
/** Wider than this and the flyover reads as a static overview, not a flight. */
const MAX_SPAN_METERS = 320;

/** Glide pace: fifteen milliseconds of flight per metre of hole. */
const MS_PER_METER = 15;
const MIN_GLIDE_MS = 4000;
const MAX_GLIDE_MS = 8000;
const HOLD_MS = 700;

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

/** Cosine ease: still at the tee, fastest mid-hole, settling onto the green. */
const easeInOut = (t: number) => (1 - Math.cos(Math.PI * clamp(t, 0, 1))) / 2;

/**
 * Builds the camera path for one hole.
 *
 * The bearing and span are fixed for the whole flight — the hole plays bottom-to-top the
 * way every hole view draws it, and a constant span keeps the tile zoom level constant so
 * panning reuses mounted tiles instead of refetching a new pyramid level every frame. Only
 * the centre moves: from the tee to the green, eased at both ends.
 */
export const buildHoleCameraPath = ({
  tee,
  green,
}: {
  tee: LatLon;
  green: LatLon;
}): HoleCameraPath => {
  const holeLengthMeters = distanceMeters(tee, green);
  const bearing = bearingDegrees(tee, green);
  const spanMeters = clamp(
    holeLengthMeters * SPAN_SHARE_OF_LENGTH,
    MIN_SPAN_METERS,
    MAX_SPAN_METERS
  );
  const durationMs = clamp(holeLengthMeters * MS_PER_METER, MIN_GLIDE_MS, MAX_GLIDE_MS);

  const cameraAt = (tMs: number): HoleCameraFrame => {
    const progress = easeInOut(tMs / durationMs);
    return {
      center: {
        lat: tee.lat + (green.lat - tee.lat) * progress,
        lon: tee.lon + (green.lon - tee.lon) * progress,
      },
      spanMeters,
      bearingDegrees: bearing,
    };
  };

  return {
    durationMs,
    holdMs: HOLD_MS,
    totalMs: durationMs + HOLD_MS,
    holeLengthMeters,
    cameraAt,
  };
};
